import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useProfilesApi } from '@/hooks/profiles/useProfilesApi';
import { useLanguage } from '@/context/language-context';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Filter, MapPin, X } from 'lucide-react';
import { FilterOptions } from '@/components/explore/types';
import ProfileList from '@/components/explore/ProfileList';
import SearchResultsSummary from '@/components/explore/SearchResultsSummary';
import { getProfileImageUrl, getInitialProfileImageUrl } from '@/services/profileImage/profileImageService';
import ProfileCard from '@/components/profile/ProfileCard';
import { EscortProfile } from '@/types';

const Explore: React.FC = () => {
  const { t, getUrlWithLanguage } = useLanguage();
  const {
    profiles,
    loading: profilesLoading,
    error: profilesError,
  } = useProfilesApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const params = new URLSearchParams(location.search);
  
  const [searchQuery, setSearchQuery] = useState(params.get('q') || '');
  const [filters, setFilters] = useState({
    gender: params.get('gender') || '',
    ageMin: params.get('ageMin') ? parseInt(params.get('ageMin') as string) : 18,
    ageMax: params.get('ageMax') ? parseInt(params.get('ageMax') as string) : 99,
    verifiedOnly: params.get('verified') === 'true',
    onlineOnly: params.get('online') === 'true',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [storageAvailable, setStorageAvailable] = useState<boolean | null>(true);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [profileImages, setProfileImages] = useState<Record<string, string>>({});
  
  useEffect(() => {
    const checkStorage = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('escort-photos')
          .list('', { limit: 1 });
          
        setStorageAvailable(!error);
        
        if (error) {
          console.warn('Storage might not be available:', error.message);
        }
      } catch (error) {
        console.error('Error checking storage availability:', error);
        setStorageAvailable(false);
      }
    };
    
    checkStorage();
  }, []);
  
  useEffect(() => {
    const newParams = new URLSearchParams();
    
    if (searchQuery) newParams.set('q', searchQuery);
    if (filters.gender) newParams.set('gender', filters.gender);
    if (filters.ageMin !== 18) newParams.set('ageMin', filters.ageMin.toString());
    if (filters.ageMax !== 99) newParams.set('ageMax', filters.ageMax.toString());
    if (filters.verifiedOnly) newParams.set('verified', 'true');
    if (filters.onlineOnly) newParams.set('online', 'true');
    
    const newSearch = newParams.toString();
    if (location.search !== `?${newSearch}`) {
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      }, { replace: true });
    }
  }, [searchQuery, filters, navigate, location.pathname, location.search]);
  
  // Log the profiles to debug
  console.log('Profiles before filtering:', profiles);
  
  // Apply filters to the profiles
  const filteredProfiles = useMemo(() => {
    if (!profiles?.length) return [];
    
    // Filter by search term (in displayName or bio)
    return profiles.filter((profile) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesQuery = 
          profile.displayName?.toLowerCase().includes(query) ||
          profile.bio?.short?.toLowerCase().includes(query) ||
          profile.location?.city?.toLowerCase().includes(query) ||
          profile.location?.country?.toLowerCase().includes(query);
        
        if (!matchesQuery) return false;
      }
      
      if (filters.gender && profile.attributes?.gender !== filters.gender) {
        return false;
      }
      
      if (profile.age < filters.ageMin || profile.age > filters.ageMax) {
        return false;
      }
      
      if (filters.verifiedOnly && !profile.isVerified) {
        return false;
      }
      
      if (filters.onlineOnly && !profile.isOnline) {
        return false;
      }
      
      return true;
    });
  }, [profiles, searchQuery, filters]);
  
  // Log the filtered profiles to debug
  console.log('Filtered profiles:', filteredProfiles);
  
  // Initialize image cache for each profile
  useEffect(() => {
    const loadImages = async () => {
      console.log("Démarrage du chargement des images pour", profiles.length, "profils");
      
      try {
        // Utilisation de Promise.all pour charger les images en parallèle
        const imagePromises = profiles.map(profile => 
          getProfileImageUrl(profile.id, profile) // Utilisation de profile.id et profile
            .then(url => {
              console.log(`Image chargée pour ${profile.id}: ${url}`);
              return { [profile.id]: url }; // Retourne un objet { profileId: url }
            })
            .catch(error => {
              console.error(`Error loading image for profile ${profile.id}:`, error);
              // Retourne un placeholder en cas d'erreur
              return { [profile.id]: getInitialProfileImageUrl(profile) }; 
            })
        );

        // Attendre que toutes les promesses soient résolues
        const settledImages = await Promise.all(imagePromises);

        // Fusionner les résultats dans un seul objet
        const images = settledImages.reduce((acc, current) => ({ ...acc, ...current }), {});
        
        console.log('Loaded profile images:', images);
        
        // Vérification des URLs avant de mettre à jour l'état
        Object.entries(images).forEach(([id, url]) => {
          if (!url || (typeof url === 'string' && !url.startsWith('http'))) {
            console.warn(`URL invalide pour le profil ${id}: ${url}`);
          }
        });
        
        setProfileImages(images);
      } catch (error) {
        console.error("Erreur globale lors du chargement des images:", error);
      }
    };
    
    if (profiles.length > 0) {
      loadImages();
    }
  }, [profiles]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };
  
  const clearSearch = () => {
    setSearchQuery('');
  };
  
  const resetFilters = () => {
    setSearchQuery('');
    setFilters({
      gender: '',
      ageMin: 18,
      ageMax: 99,
      verifiedOnly: false,
      onlineOnly: false,
    });
  };
  
  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const toggleFavorite = async (profileId: string) => {
    if (!user) {
      toast({
        title: t('common.login_required'),
        description: t('common.login_to_favorite'),
        variant: 'default',
      });
      navigate(getUrlWithLanguage('/login'));
      return;
    }
    
    try {
      // Check if already favorite
      const { data: existingFavorite, error: checkError } = await supabase
        .from('user_favorites') // Use 'user_favorites' table
        .select('profile_id') // Select 'profile_id'
        .match({ user_id: user.id, profile_id: profileId }); // Match 'profile_id'

      if (checkError) throw checkError;

      if (existingFavorite && existingFavorite.length > 0) {
        // Already favorite, remove it
        const { error: deleteError } = await supabase
          .from('user_favorites') // Use 'user_favorites' table
          .delete()
          .match({ user_id: user.id, profile_id: profileId }); // Match 'profile_id'
        if (deleteError) throw deleteError;
        // Update local state
        setFavorites(prev => { const next = new Set(prev); next.delete(profileId); return next; });
        toast({ title: t('profile.removed_from_favorites') });
      } else {
        // Not favorite, add it
        const { error: insertError } = await supabase
          .from('user_favorites') // Use 'user_favorites' table
          .insert({ user_id: user.id, profile_id: profileId }); // Insert 'profile_id'
        if (insertError) throw insertError;
        // Update local state
        setFavorites(prev => new Set(prev).add(profileId));
        toast({ title: t('profile.added_to_favorites') });
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
      // Revert optimistic update on error
      setFavorites(favorites);
      toast({
        title: t('common.error'),
        description: t('search:error_updating_favorites'),
        variant: 'destructive',
      });
    }
  };
  
  // Calculate active filters count
  const calculateActiveFiltersCount = () => {
    let count = 0;
    if (filters.gender) count++;
    if (filters.ageMin !== 18) count++;
    if (filters.ageMax !== 99) count++;
    if (filters.verifiedOnly) count++;
    if (filters.onlineOnly) count++;
    return count;
  };
  const activeFiltersCount = calculateActiveFiltersCount();

  // Fetch favorites on component mount and when user changes
  useEffect(() => {
    if (user) {
      // Fetch initial favorites
      supabase
        .from('user_favorites') // Use 'user_favorites' table
        .select('profile_id') // Select 'profile_id'
        .eq('user_id', user.id)
        .then(({ data, error }) => {
          if (error) console.error('Error fetching initial favorites:', error);
          else if (data) {
            setFavorites(new Set(data.map(fav => fav.profile_id))); // Map 'profile_id'
          }
        });
    }
  }, [user]);

  // Define isGeneratingImage function (placeholder)
  const isGeneratingImage = (profileId: string): boolean => {
    // TODO: Implement actual logic if image generation is a feature
    // console.warn(`isGeneratingImage called for ${profileId}, returning false (placeholder)`);
    return false;
  };

  // Define onGenerateImage function (placeholder)
  const handleGenerateImage = async (profile: EscortProfile, showToast?: boolean) => {
    console.warn(`onGenerateImage called for ${profile.id}, not implemented`, { showToast });
    // TODO: Implement actual image generation logic
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-6 flex gap-2 items-center">
          <div className="relative flex-grow">
            <Input
              type="text"
              placeholder={t('search:search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            {searchQuery && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 px-2"
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Button 
            type="submit" 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4" />
            {t('search:filters')}
          </Button>
        </form>
        
        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Gender Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('search:gender')}</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">{t('search:any')}</option>
                  <option value="female">{t('search:female')}</option>
                  <option value="male">{t('search:male')}</option>
                  <option value="transgender">{t('search:transgender')}</option>
                </select>
              </div>
              
              {/* Age Range Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('search:age_range')}</label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    min="18"
                    max="99"
                    value={filters.ageMin}
                    onChange={(e) => handleFilterChange('ageMin', parseInt(e.target.value))}
                    className="w-1/2"
                  />
                  <span>-</span>
                  <Input
                    type="number"
                    min="18"
                    max="99"
                    value={filters.ageMax}
                    onChange={(e) => handleFilterChange('ageMax', parseInt(e.target.value))}
                    className="w-1/2"
                  />
                </div>
              </div>
              
              {/* Boolean Filters */}
              <div className="flex flex-col space-y-2 justify-end">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="verifiedOnly"
                    checked={filters.verifiedOnly} 
                    onCheckedChange={(checked) => handleFilterChange('verifiedOnly', checked)} 
                  />
                  <label htmlFor="verifiedOnly">{t('search:verified_only')}</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="onlineOnly"
                    checked={filters.onlineOnly} 
                    onCheckedChange={(checked) => handleFilterChange('onlineOnly', checked)} 
                  />
                  <label htmlFor="onlineOnly">{t('search:online_now')}</label>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetFilters}
                  className="self-start text-sm mt-2"
                >
                  {t('search:reset_filters')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Search Results Summary */}
        <SearchResultsSummary 
          resultsCount={filteredProfiles.length} 
          isLoading={profilesLoading} 
          searchQuery={searchQuery}
          activeFiltersCount={activeFiltersCount}
        />
        
        {/* Profile List */}
        <ProfileList
          profiles={filteredProfiles}
          profileImages={profileImages}
          favorites={favorites}
          isLoading={profilesLoading}
          onToggleFavorite={toggleFavorite}
          storageAvailable={storageAvailable}
          searchQuery={searchQuery}
          onClearSearch={clearSearch}
          isGeneratingImage={isGeneratingImage}
          onGenerateImage={handleGenerateImage}
        />
      </div>
    </Layout>
  );
};

export default Explore;
